
// ============================================================================
// Type Definitions
// ============================================================================

import { DateTime } from "luxon";

export interface PayerInfo {
	email: string;
	phone?: string;
	name: string;
	datetime: string;
}

export interface UserData {
	id: string;
	email: string;
	mobile?: string;
	alternativePaypalEmails: string[];
}

export interface EventData {
	id: number;
	date: string;
	total_cost_cents: number;
	participant_count: number;
	attendee_user_ids: string[];
}

export interface MatchingResult {
	matchedUserId: string | null;
	matchedEventId: number | null;
	costPerParticipant: number | null;
}

// ============================================================================
// User Matching Logic
// ============================================================================

/**
 * Matches a payer to a user by checking:
 * 1. Primary email match (case-insensitive)
 * 2. Mobile/phone match
 * 3. Alternative PayPal emails
 *
 * Returns the matched user ID or null if no match found.
 */
export function matchPayerToUser(
	payerInfo: PayerInfo,
	users: UserData[],
): string | null {
	const payerEmailLower = payerInfo.email.toLowerCase();
	const payerPhoneLower = (payerInfo.phone || "").toLowerCase().trim();

	// First pass: Primary email match
	for (const u of users) {
		if (u.email.toLowerCase() === payerEmailLower) {
			return u.id;
		}
	}

	// Second pass: Mobile/phone match (only if payer has a phone)
	if (payerPhoneLower) {
		for (const u of users) {
			if (u.mobile && u.mobile.toLowerCase().trim() === payerPhoneLower) {
				return u.id;
			}
		}
	}

	// Third pass: Alternative PayPal emails
	for (const u of users) {
		for (const altEmail of u.alternativePaypalEmails) {
			if (altEmail.toLowerCase() === payerEmailLower) {
				return u.id;
			}
		}
	}

	// No match found
	return null;
}

// ============================================================================
// Event Matching Logic
// ============================================================================

/**
 * Matches a payment to an event by finding the most recent event that:
 * 1. Occurred on or before the payment date
 * 2. The user attended (based on attendee_user_ids)
 *
 * Returns the matched event ID or null if no suitable event found.
 */
export function matchPaymentToEvent(
	paymentDatetime: string,
	userId: string | null,
	events: EventData[],
): number | null {
	if (!userId) {
		return null;
	}

	// Parse payment datetime using Luxon (respects timezone info)
	const paymentDt = DateTime.fromISO(paymentDatetime);
	if (!paymentDt.isValid) {
		// Unable to parse payment datetime -> no match
		return null;
	}

	// Use date-only (YYYY-MM-DD) for comparisons to match event.date format
	const paymentDateString = paymentDt.toISODate();

	// Filter events that:
	// 1. Occurred on or before the payment date
	// 2. User attended
	const validEvents = events
		.filter((event) => {
			// Parse event date (accepts YYYY-MM-DD or full ISO)
			const eventDt = DateTime.fromISO(event.date);
			if (!eventDt.isValid) {
				return false;
			}

			// Compare date-only strings
			if (eventDt.toISODate() > paymentDateString) {
				return false;
			}

			// Check if user attended the event
			return event.attendee_user_ids.includes(userId);
		})
		// Sort by date descending (most recent first). date strings in YYYY-MM-DD order lexicographically.
		.sort((a, b) => b.date.localeCompare(a.date));

	// Return the most recent matching event
	return validEvents.length > 0 ? validEvents[0]?.id ?? null : null;
}

// ============================================================================
// Cost Calculation
// ============================================================================

/**
 * Calculates the expected cost per participant from raw totals.
 * Returns null if there are no participants.
 */
export function calculateCostPerParticipantFromTotals(
	totalCostCents: number,
	participantCount: number,
): number | null {
	if (participantCount === 0) return null;
	// Always round up to the next cent when splitting costs among participants
	return Math.ceil(totalCostCents / participantCount);
}

/**
 * Backwards-compatible helper that accepts the full event object.
 * Internally delegates to the totals-based helper so callers that
 * only need totals don't have to pass the whole event.
 */
export function calculateCostPerParticipant(
	event: EventData,
): number | null {
	return calculateCostPerParticipantFromTotals(
		event.total_cost_cents,
		event.participant_count,
	);
}

// ============================================================================
// Combined Matching Function
// ============================================================================

/**
 * Performs full matching for a payment:
 * 1. Matches payer to user
 * 2. Matches payment to the most recent event the user attended
 * 3. Calculates cost per participant for the matched event
 *
 * Returns the matching result with matched user ID, event ID, and cost per participant.
 */
export function matchPayment(
	payerInfo: PayerInfo,
	users: UserData[],
	events: EventData[],
): MatchingResult {
	// Step 1: Match payer to user
	const matchedUserId = matchPayerToUser(payerInfo, users);

	// Step 2: Match to event
	const matchedEventId = matchPaymentToEvent(
		payerInfo.datetime,
		matchedUserId,
		events,
	);

	// Step 3: Calculate cost per participant
	let costPerParticipant: number | null = null;
	if (matchedEventId !== null) {
		const matchedEvent = events.find((e) => e.id === matchedEventId);
		if (matchedEvent) {
			costPerParticipant = calculateCostPerParticipant(matchedEvent);
		}
	}

	return {
		matchedUserId,
		matchedEventId,
		costPerParticipant,
	};
}

// ============================================================================
// Validation and Analysis Functions
// ============================================================================

/**
 * Analyzes a payment amount against the expected cost per participant.
 * Returns an indicator: null (fits), "warning" (too high), or "info" (too low)
 *
 * Rules:
 * - If amount < cost per participant: "info"
 * - If amount > cost per participant + €3 (300 cents): "warning"
 * - Otherwise: null (fits)
 */
export function analyzePaymentAmount(
	amountCents: number,
	costPerParticipant: number | null,
): "info" | "warning" | null {
	if (costPerParticipant === null) {
		// No event matched, can't analyze
		return null;
	}

	if (amountCents < costPerParticipant) {
		return "info";
	}

	if (amountCents > costPerParticipant + 300) {
		return "warning";
	}

	return null;
}

/**
 * Checks if a user attended a specific event.
 * Used for sanity checking during payment import.
 */
export function didUserAttendEvent(
	userId: string,
	eventId: number,
	events: EventData[],
): boolean {
	const event = events.find((e) => e.id === eventId);
	if (!event) {
		return false;
	}
	return event.attendee_user_ids.includes(userId);
}

/**
 * Validates that no duplicate payments exist for a user+event combination.
 * Used for sanity checking during payment import.
 *
 * Input: Array of payments to be imported with their user and event assignments
 * Returns: Set of row IDs that have duplicate user+event combinations
 */
export function findDuplicateUserEventCombinations(
	payments: Array<{
		rowId: string; // unique identifier for the row
		userId: string | null;
		eventId: number | null;
	}>,
): Set<string> {
	const duplicates = new Set<string>();
	const seen = new Map<string, string>();

	for (const payment of payments) {
		// Only check payments that have both user and event
		if (!payment.userId || !payment.eventId) {
			continue;
		}

		const key = `${payment.userId}:${payment.eventId}`;

		if (seen.has(key)) {
			// Found a duplicate
			duplicates.add(payment.rowId);
			duplicates.add(seen.get(key)!); // Also mark the first occurrence
		} else {
			seen.set(key, payment.rowId);
		}
	}

	return duplicates;
}

// ============================================================================
// Bulk Matching (for frontend use)
// ============================================================================

/**
 * Performs matching for multiple payments at once.
 * This is useful for batch processing during CSV import.
 *
 * Input: Array of payer infos and the user/event data
 * Returns: Array of matching results in the same order as input
 */
export function matchMultiplePayments(
	payersInfo: PayerInfo[],
	users: UserData[],
	events: EventData[],
): MatchingResult[] {
	return payersInfo.map((payerInfo) => matchPayment(payerInfo, users, events));
}
